import React, { useState, useRef, useEffect } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query"
import Header from "../../../layouts/Header"
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Paper,
  Divider,
  Collapse,
  Badge,
  IconButton,
  Alert,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  InputAdornment,
} from "@mui/material"
import { createTheme, ThemeProvider } from "@mui/material/styles"
import CustomBackdrop from "../../../components/CustomBackdrop"
import { api, showWarning, showSuccess } from "../../../api"

// Importaciones para Modo Offline
import localforage from "localforage"
import Swal from "sweetalert2"
import {
  CloudOff,
  CloudDone,
  Sync,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Search as SearchIcon,
} from "@mui/icons-material"

// Integración del componente Obturador personalizado
import Obturador from "../../../components/Obturador/Obturador"
import dayjs from "dayjs"

// Íconos
import SaveIcon from "@mui/icons-material/Save"
import ArrowBackIcon from "@mui/icons-material/ArrowBack"
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn"

const theme = createTheme({
  palette: { primary: { main: "#196C87" }, secondary: { main: "#2E7D32" } },
})

const ScannerMobileIndex = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const tareaState = location.state || {}

  // ================= ESTADOS MODO OFFLINE & COLA =================
  const [isOffline, setIsOffline] = useState(!navigator.onLine)
  const [lecturasPendientes, setLecturasPendientes] = useState(0)
  const [modalColaOpen, setModalColaOpen] = useState(false)
  const [listaCola, setListaCola] = useState([])

  // ================= ESTADOS SUGERENCIAS LOTE/SERIE =================
  const [modalLotesOpen, setModalLotesOpen] = useState(false)
  const [modalSeriesOpen, setModalSeriesOpen] = useState(false)
  const [lotesSugeridos, setLotesSugeridos] = useState([])
  const [seriesSugeridas, setSeriesSugeridas] = useState([])

  // Estado para mostrar el artículo resuelto debajo del input
  // (artcodigo real, descripción, inventario, origen del match y valor original leído)
  const [articuloResuelto, setArticuloResuelto] = useState({
    artcodigo: "",
    artdescri: "",
    invcodigo: "",
    matchOrigen: "",
    codigoLeido: "",
  })

  useEffect(() => {
    if (!tareaState.contnumero) {
      navigate(-1)
    }

    const handleOnline = () => setIsOffline(false)
    const handleOffline = () => setIsOffline(true)
    window.addEventListener("online", handleOnline)
    window.addEventListener("offline", handleOffline)

    verificarColaLocal()

    return () => {
      window.removeEventListener("online", handleOnline)
      window.removeEventListener("offline", handleOffline)
    }
  }, [tareaState, navigate])

  const verificarColaLocal = async () => {
    const cola = (await localforage.getItem("toma_fisica_offline_queue")) || []
    setLecturasPendientes(cola.length)
    setListaCola(cola)
  }

  // --- CONTROLADORES DEL GESTOR DE COLA ---
  const handleOpenCola = async () => {
    await verificarColaLocal()
    setModalColaOpen(true)
  }

  const handleEliminarDeCola = async (index) => {
    const nuevaCola = [...listaCola]
    nuevaCola.splice(index, 1)
    await localforage.setItem("toma_fisica_offline_queue", nuevaCola)
    setListaCola(nuevaCola)
    setLecturasPendientes(nuevaCola.length)
  }

  const handleEditarDeCola = async (index) => {
    const item = listaCola[index]

    setCaptura({
      artcodigo: item.artcodigo,
      cantidad: item.cantidad,
      lote: item.lote || "",
      caducidad: item.caducidad || "",
      fabricacion: item.fabricacion || "",
      serie: item.serie || "",
      sermotor: item.sermotor || "",
      serram: item.serram || "",
      serModAnio: item.serModAnio || "",
      serProAnio: item.serProAnio || "",
      serProMes: item.serProMes || "",
      colcodigo: item.colcodigo || "",
      paiscodigo: item.paiscodigo || "",
    })

    setShowMetadata(true)
    setTargetField("artcodigo")

    await handleEliminarDeCola(index)
    setModalColaOpen(false)
    showSuccess("Registro recuperado. Complete los datos faltantes y vuelva a guardar.")

    setTimeout(() => {
      if (cantidadInputRef.current) cantidadInputRef.current.focus()
    }, 500)
  }

  // --- QUERIES PARA COMBOS DE VEHÍCULOS ---
  const { data: colores = [] } = useQuery({
    queryKey: ["coloresVehiculos"],
    queryFn: async () => {
      try {
        const res = await api.get("/TomaFisica/getColores")
        return res?.data?.data || []
      } catch (error) {
        return []
      }
    },
    enabled: true,
  })

  const { data: paises = [] } = useQuery({
    queryKey: ["paisesVehiculos"],
    queryFn: async () => {
      try {
        const res = await api.get("/TomaFisica/getPaises")
        return res?.data?.data || []
      } catch (error) {
        return []
      }
    },
    enabled: true,
  })

  // Referencias para salto de foco ágil (UX)
  const scannerInputRef = useRef(null)
  const cantidadInputRef = useRef(null)
  const loteInputRef = useRef(null)
  const caducidadInputRef = useRef(null)
  const serieInputRef = useRef(null)

  const [targetField, setTargetField] = useState("artcodigo")

  const [captura, setCaptura] = useState({
    artcodigo: "",
    cantidad: 1,
    lote: "",
    caducidad: "",
    fabricacion: "",
    serie: "",
    sermotor: "",
    serram: "",
    serModAnio: "",
    serProAnio: "",
    serProMes: "",
    colcodigo: "",
    paiscodigo: "",
  })

  const [reglasArticulo, setReglasArticulo] = useState({
    lote: false,
    serie: false,
    esVehiculo: false,
    sugiereLoteSerie: false,
  })
  const [showMetadata, setShowMetadata] = useState(false)

  const limpiarFormulario = () => {
    setCaptura({
      artcodigo: "",
      cantidad: 1,
      lote: "",
      caducidad: "",
      fabricacion: "",
      serie: "",
      sermotor: "",
      serram: "",
      serModAnio: "",
      serProAnio: "",
      serProMes: "",
      colcodigo: "",
      paiscodigo: "",
    })
    setReglasArticulo({ lote: false, serie: false, esVehiculo: false, sugiereLoteSerie: false })
    setShowMetadata(false)
    setTargetField("artcodigo")
    // Limpiar el panel del artículo resuelto
    setArticuloResuelto({
      artcodigo: "",
      artdescri: "",
      invcodigo: "",
      matchOrigen: "",
      codigoLeido: "",
    })
    setTimeout(() => {
      if (scannerInputRef.current) scannerInputRef.current.focus()
    }, 300)
  }

  // 1. MUTACIÓN: VALIDACIÓN PROACTIVA (Online)
  const { mutateAsync: mutValidarArticulo, isPending: isValidating } = useMutation({
    mutationFn: async (codigo) => {
      return await api.post("/TomaFisica/validarArticuloMobile", {
        tomnumero: tareaState.tomnumero,
        bodcodigo: tareaState.bodcodigo,
        artcodigo: codigo,
      })
    },
    onSuccess: (res) => {
      const data = res?.data?.data || {}
      const exigeLote = data.artlote === -1 || String(data.artlote).toUpperCase() === "S"
      const exigeSerie = data.artserie === -1 || data.artseriedesp === -1 || String(data.artserie).toUpperCase() === "S"
      const esVehiculo = data.TIPSERIE === "V"

      const sugiereLoteSerie = data.TomSugLotSer === -1 || data.TomSugLotSer === true

      setReglasArticulo({ lote: exigeLote, serie: exigeSerie, esVehiculo, sugiereLoteSerie })
      setShowMetadata(exigeLote || exigeSerie)

      // Guardar la información del artículo resuelto:
      // - codigoLeido viene del valor original escaneado
      // - matchOrigen indica si se resolvió por ARTICULO, BARRAS o SERIE
      const codigoEscaneadoOriginal = res?.config?.data ? JSON.parse(res.config.data).artcodigo : ""
      setArticuloResuelto({
        artcodigo: data.artcodigo || "",
        artdescri: data.artdescri || "",
        invcodigo: data.invcodigo || "",
        matchOrigen: data.matchOrigen || "",
        codigoLeido: codigoEscaneadoOriginal || "",
      })

      // Reemplazar el input "Escanear Código" por el artcodigo real resuelto
      setCaptura((prev) => ({ ...prev, artcodigo: data.artcodigo || prev.artcodigo }))

      // Si el valor leído fue una SERIE, consultar y autocompletar datos de esa serie
      if (data.matchOrigen === "SERIE") {
        mutConsultarSeriesSugeridasPorEscaneo(data.artcodigo, codigoEscaneadoOriginal)
      }

      if (exigeSerie) setCaptura((prev) => ({ ...prev, cantidad: 1 }))

      setTimeout(() => {
        if (cantidadInputRef.current) {
          cantidadInputRef.current.focus()
          cantidadInputRef.current.select()
        }
      }, 100)
    },
    onError: (err) => {
      showWarning(err?.response?.data?.message || "El artículo no pertenece al tomo o no existe.")
      setCaptura((prev) => ({ ...prev, artcodigo: "" }))
      setShowMetadata(false)
      // Limpiar el panel del artículo resuelto en caso de error
      setArticuloResuelto({
        artcodigo: "",
        artdescri: "",
        invcodigo: "",
        matchOrigen: "",
        codigoLeido: "",
      })
      setTimeout(() => {
        if (scannerInputRef.current) scannerInputRef.current.focus()
      }, 100)
    },
  })

  // MUTACIÓN CONSULTAR SUGERENCIAS LOTES
  const { mutateAsync: mutConsultarLotesSugeridos, isPending: isLoadingLotes } = useMutation({
    mutationFn: async () => {
      return await api.post("/TomaFisica/getLotesSugeridos", {
        artcodigo: captura.artcodigo,
        bodcodigo: tareaState.bodcodigo,
      })
    },
    onSuccess: (res) => {
      const lotes = res?.data?.data || []
      if (lotes.length === 0) return showWarning("No se encontraron lotes con saldo para este artículo.")
      setLotesSugeridos(lotes)
      setModalLotesOpen(true)
    },
    onError: () => {
      showWarning("Error al consultar lotes sugeridos.")
    },
  })

  // MUTACIÓN CONSULTAR SUGERENCIAS SERIES (consulta manual desde el botón)
  const { mutateAsync: mutConsultarSeriesSugeridas, isPending: isLoadingSeries } = useMutation({
    mutationFn: async () => {
      return await api.post("/TomaFisica/getSeriesSugeridas", {
        artcodigo: captura.artcodigo,
        bodcodigo: tareaState.bodcodigo,
      })
    },
    onSuccess: (res) => {
      const series = res?.data?.data || []
      if (series.length === 0) return showWarning("No se encontraron series activas para este artículo.")
      setSeriesSugeridas(series)
      setModalSeriesOpen(true)
    },
    onError: () => {
      showWarning("Error al consultar series sugeridas.")
    },
  })

  // Consulta automática de la serie escaneada (solo cuando matchOrigen === "SERIE")
  // Llama a getSeriesSugeridas, filtra por sernumero === codigoLeido y autocompleta captura.
  const mutConsultarSeriesSugeridasPorEscaneo = async (artcodigo, codigoLeido) => {
    try {
      const res = await api.post("/TomaFisica/getSeriesSugeridas", {
        artcodigo,
        bodcodigo: tareaState.bodcodigo,
      })
      const series = res?.data?.data || []

      if (series.length === 0) {
        showWarning("No se encontraron series activas para este artículo.")
        setShowMetadata(true)
        return
      }

      // Buscar la serie exacta que el operario escaneó
      const serieEncontrada = series.find((s) => s.sernumero === codigoLeido)

      if (!serieEncontrada) {
        // Si no se encontró la serie, avisar y dejar el panel abierto para llenar manualmente
        showWarning("No se pudieron recuperar los datos de la serie escaneada. Complete manualmente.")
        setShowMetadata(true)
        return
      }

      // Autocompletar los datos de la serie encontrada
      setCaptura((prev) => ({
        ...prev,
        serie: serieEncontrada.sernumero || "",
        sermotor: serieEncontrada.sermotor || "",
        serram: serieEncontrada.serram || "",
        serModAnio: serieEncontrada.serModAnio ?? "",
        serProAnio: serieEncontrada.serProAnio ?? "",
        serProMes: serieEncontrada.serProMes ?? "",
        colcodigo: serieEncontrada.colcodigo || "",
        paiscodigo: serieEncontrada.paiscodigo || "",
        cantidad: 1,
      }))
    } catch (error) {
      showWarning("Error al consultar los datos de la serie escaneada.")
      setShowMetadata(true)
    }
  }

  // 2. MUTACIÓN: GUARDAR LECTURA (Online)
  const { mutateAsync: mutRegistrarLectura, isPending: isSaving } = useMutation({
    mutationFn: async (payload) => {
      return await api.post("/TomaFisica/registrarLecturaMobile", payload)
    },
    onSuccess: (res) => {
      let msgSuccess = res?.data?.data
      if (typeof msgSuccess === "object") msgSuccess = msgSuccess?.data || msgSuccess?.message || "Artículo registrado."
      showSuccess(typeof msgSuccess === "string" ? msgSuccess : "Artículo registrado.")
      limpiarFormulario()
    },
    onError: async (err) => {
      if (err.message === "Network Error" || err.code === "ECONNABORTED") {
        await guardarOffline(JSON.parse(err.config.data))
      } else {
        showWarning(err?.response?.data?.message || "Error al registrar artículo")
        setTimeout(() => {
          if (scannerInputRef.current) scannerInputRef.current.focus()
        }, 300)
      }
    },
  })

  const handleInputChange = (field, value) => {
    setCaptura((prev) => ({ ...prev, [field]: typeof value === "string" ? value.toUpperCase() : value }))
  }

  const procesarEscaneoArticulo = async (scannedCode) => {
    if (isOffline) {
      showWarning("Modo Offline: Si el artículo requiere Lote/Serie despliegue el panel manualmente.")
      setTimeout(() => {
        if (cantidadInputRef.current) {
          cantidadInputRef.current.focus()
          cantidadInputRef.current.select()
        }
      }, 100)
    } else {
      // Limpiar el panel del artículo resuelto al iniciar una nueva validación
      setArticuloResuelto({
        artcodigo: "",
        artdescri: "",
        invcodigo: "",
        matchOrigen: "",
        codigoLeido: "",
      })
      await mutValidarArticulo(scannedCode)
    }
  }

  // --- CONTROLADORES DE TECLADO ---
  const handleBarcodeKeyDown = async (e) => {
    if (e.key === "Enter") {
      e.preventDefault()
      const code = captura.artcodigo.trim()
      if (code) await procesarEscaneoArticulo(code)
      else showWarning("Ingrese un código de barras")
    }
  }

  const handleCantidadKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault()
      if (reglasArticulo.lote && loteInputRef.current) loteInputRef.current.focus()
      else if (reglasArticulo.serie && serieInputRef.current) serieInputRef.current.focus()
      else handleSubmit()
    }
  }

  const handleObturadorScan = async (infoRead, handleCloseModalQr) => {
    const scannedCode = infoRead?.barcodeDataText?.trim()
    if (scannedCode) {
      handleInputChange(targetField, scannedCode)
      handleCloseModalQr()

      if (targetField === "artcodigo") await procesarEscaneoArticulo(scannedCode)
      else if (targetField === "lote") {
        setTimeout(() => {
          if (caducidadInputRef.current) caducidadInputRef.current.focus()
        }, 300)
      }
    } else {
      handleCloseModalQr()
    }
  }

  const guardarOffline = async (payload) => {
    const cola = (await localforage.getItem("toma_fisica_offline_queue")) || []
    cola.push(payload)
    await localforage.setItem("toma_fisica_offline_queue", cola)
    setLecturasPendientes(cola.length)
    setListaCola(cola)
    showSuccess("Red Inestable: Lectura guardada localmente (Offline)")
    limpiarFormulario()
  }

  const handleSubmit = async () => {
    if (!captura.artcodigo.trim()) return showWarning("Debe escanear un código de artículo.")
    if (captura.cantidad <= 0) return showWarning("La cantidad debe ser mayor a cero.")

    if (reglasArticulo.lote && (!captura.lote.trim() || !captura.caducidad.trim())) {
      return showWarning("Este artículo exige registrar el Lote y Fecha de Caducidad.")
    }
    if (reglasArticulo.serie) {
      if (!captura.serie.trim()) return showWarning("Este artículo exige escanear el Número de Serie.")
      if (reglasArticulo.esVehiculo) {
        if (!captura.sermotor.trim()) return showWarning("Debe ingresar el número de Motor del vehículo.")
        if (!captura.colcodigo) return showWarning("Debe seleccionar el Color del vehículo.")
      }
    }

    const payload = {
      contnumero: tareaState.contnumero,
      tomnumero: tareaState.tomnumero,
      // Priorizar invcodigo de la tarea; si no existe, usar el resuelto por la validación
      invcodigo: tareaState.invcodigo || articuloResuelto.invcodigo,
      bodcodigo: tareaState.bodcodigo,
      artcodigo: captura.artcodigo.trim(),
      cantidad: parseFloat(captura.cantidad),
      lote: captura.lote.trim(),
      fabricacion: captura.fabricacion ? dayjs(captura.fabricacion).format("YYYY-MM-DD") : null,
      caducidad: captura.caducidad ? dayjs(captura.caducidad).format("YYYY-MM-DD") : null,
      serie: captura.serie.trim(),
      sermotor: captura.sermotor.trim(),
      serram: captura.serram.trim(),
      serModAnio: captura.serModAnio ? parseInt(captura.serModAnio) : null,
      serProAnio: captura.serProAnio ? parseInt(captura.serProAnio) : null,
      serProMes: captura.serProMes ? parseInt(captura.serProMes) : null,
      colcodigo: captura.colcodigo,
      paiscodigo: captura.paiscodigo,
    }

    if (isOffline) {
      await guardarOffline(payload)
    } else {
      await mutRegistrarLectura(payload)
    }
  }

  const sincronizarCola = async () => {
    if (isOffline) return Swal.fire("Aún sin conexión", "Busque un lugar con señal WiFi o Celular.", "warning")
    if (listaCola.length === 0) return showSuccess("Todo al día. No hay lecturas pendientes.")

    Swal.fire({
      title: "Sincronizando...",
      text: `Subiendo ${listaCola.length} lecturas al servidor`,
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading(),
    })

    let subidas = 0
    const colaRestante = []

    for (let i = 0; i < listaCola.length; i++) {
      try {
        await api.post("/TomaFisica/registrarLecturaMobile", listaCola[i])
        subidas++
      } catch (err) {
        console.error("Fallo al subir lectura", err)
        colaRestante.push(listaCola[i])
      }
    }

    await localforage.setItem("toma_fisica_offline_queue", colaRestante)
    setLecturasPendientes(colaRestante.length)
    setListaCola(colaRestante)

    if (colaRestante.length === 0) {
      Swal.fire("¡Sincronización Completa!", `Se subieron ${subidas} lecturas correctamente.`, "success")
      setModalColaOpen(false)
    } else {
      Swal.fire({
        icon: "warning",
        title: "Sincronización Parcial",
        text: `Se subieron ${subidas} lecturas. Quedan ${colaRestante.length} fallidas en la cola. Corríjalas usando el botón "Editar".`,
      })
    }
  }

  const handleTerminarTarea = () => {
    if (lecturasPendientes > 0) {
      return Swal.fire("Alto", "Debe sincronizar las lecturas pendientes antes de salir.", "error")
    }
    if (window.confirm("¿Está seguro de regresar a la bandeja?")) {
      queryClient.invalidateQueries(["getTomosAsignadosMobile"])
      navigate(-1)
    }
  }

  const getTargetName = () => {
    if (targetField === "lote") return "LOTE"
    if (targetField === "serie") return "SERIE"
    return "ARTÍCULO"
  }

  return (
    <ThemeProvider theme={theme}>
      <Header />
      <div className="main main-app p-2" style={{ backgroundColor: "#f0f2f5", minHeight: "100vh" }}>
        <CustomBackdrop isLoading={isSaving || isValidating || isLoadingLotes || isLoadingSeries} />

        <Box sx={{ maxWidth: "500px", margin: "0 auto", pb: 10 }}>
          <Box display="flex" justifyContent="space-between" mb={2}>
            <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)} sx={{ fontWeight: "bold" }}>
              Volver
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<AssignmentTurnedInIcon />}
              onClick={handleTerminarTarea}
              sx={{ fontWeight: "bold" }}
            >
              Terminar Ruta
            </Button>
          </Box>

          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
            p={1.5}
            bgcolor="white"
            borderRadius={2}
            boxShadow={1}
          >
            <Box display="flex" alignItems="center">
              {isOffline ? (
                <CloudOff color="error" sx={{ fontSize: 32, mr: 1 }} />
              ) : (
                <CloudDone color="success" sx={{ fontSize: 32, mr: 1 }} />
              )}
              <Typography variant="subtitle2" fontWeight="bold" color={isOffline ? "error" : "success.main"}>
                {isOffline ? "MODO OFFLINE" : "CONECTADO"}
              </Typography>
            </Box>
            <Tooltip title="Gestionar lecturas guardadas en el celular">
              <Badge badgeContent={lecturasPendientes} color="secondary">
                <IconButton onClick={handleOpenCola} disabled={lecturasPendientes === 0} sx={{ bgcolor: "#e3f2fd" }}>
                  <Sync color={lecturasPendientes > 0 ? "primary" : "disabled"} />
                </IconButton>
              </Badge>
            </Tooltip>
          </Box>

          {isOffline && (
            <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
              <b>SIN INTERNET:</b> Las lecturas se guardarán en su equipo. <b>NO BORRE EL CACHÉ.</b>
            </Alert>
          )}

          <Paper elevation={4} sx={{ p: 2, mb: 2, borderRadius: 3, bgcolor: "#196C87", color: "white" }}>
            <Typography variant="caption" sx={{ opacity: 0.8 }}>
              Tomo: {tareaState.tomnumero}
            </Typography>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ lineHeight: 1.2 }}>
              {tareaState.tomdescripcion}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.9 }}>
              Bodega: {tareaState.bodcodigo}
            </Typography>
          </Paper>

          <Paper elevation={3} sx={{ p: 3, borderRadius: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  inputRef={scannerInputRef}
                  fullWidth
                  label="Escanear Código"
                  variant="outlined"
                  value={captura.artcodigo}
                  onFocus={() => setTargetField("artcodigo")}
                  onChange={(e) => handleInputChange("artcodigo", e.target.value)}
                  onKeyDown={handleBarcodeKeyDown}
                  autoFocus
                  // 1. Engañar a Gboard para forzar la tecla GO y 2. Agregar Lupa Física
                  inputProps={{
                    enterKeyHint: "go",
                    style: { fontSize: "1.2rem", fontWeight: "bold" },
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => {
                            const code = captura.artcodigo.trim()
                            if (code) procesarEscaneoArticulo(code)
                            else showWarning("Ingrese un código de barras")
                          }}
                          edge="end"
                          color="primary"
                        >
                          <SearchIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  placeholder="Ej: 786100..."
                />
              </Grid>

              {/* Panel con el artículo resuelto debajo del input */}
              {articuloResuelto.artcodigo && (
                <Grid item xs={12}>
                  <Box
                    sx={{
                      p: 1.5,
                      bgcolor: "#e8f5e9",
                      borderRadius: 2,
                      border: "1px solid #66bb6a",
                    }}
                  >
                    <Typography variant="subtitle2" fontWeight="bold" color="success.dark">
                      {articuloResuelto.artdescri || "(sin descripción)"}
                    </Typography>
                    <Typography variant="caption" color="textSecondary" display="block">
                      Código: <b>{articuloResuelto.artcodigo}</b>
                      {articuloResuelto.invcodigo && ` · Inventario: ${articuloResuelto.invcodigo}`}
                      {articuloResuelto.matchOrigen && (
                        <span style={{ marginLeft: 8, color: "#196C87" }}>
                          · Resuelto por: <b>{articuloResuelto.matchOrigen}</b>
                        </span>
                      )}
                    </Typography>
                    {/* Mostrar el valor original leído solo si NO fue resuelto por ARTICULO */}
                    {articuloResuelto.matchOrigen !== "ARTICULO" && articuloResuelto.codigoLeido && (
                      <Typography variant="caption" color="textSecondary" display="block">
                        Leído: <b>{articuloResuelto.codigoLeido}</b>
                      </Typography>
                    )}
                  </Box>
                </Grid>
              )}

              <Grid item xs={12} display="flex" justifyContent="center">
                <Obturador
                  scannerParams={{ codeType: "barcode", msg: `Escaneando código para: ${getTargetName()}` }}
                  executeFunction={handleObturadorScan}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  inputRef={cantidadInputRef}
                  fullWidth
                  type="number"
                  label="Cantidad Física"
                  variant="outlined"
                  value={captura.cantidad}
                  onChange={(e) => handleInputChange("cantidad", e.target.value)}
                  onKeyDown={handleCantidadKeyDown}
                  disabled={reglasArticulo.serie}
                  inputProps={{
                    min: 1,
                    step: "any",
                    enterKeyHint: "next",
                    style: { fontSize: "1.5rem", textAlign: "center", fontWeight: "bold" },
                  }}
                />
              </Grid>

              {isOffline && !showMetadata && (
                <Grid item xs={12} textAlign="center">
                  <Button variant="text" color="warning" onClick={() => setShowMetadata(true)}>
                    + Forzar panel de Lotes y Series
                  </Button>
                </Grid>
              )}

              <Grid item xs={12}>
                <Collapse in={showMetadata}>
                  <Box mt={1} p={2} bgcolor="#ffebee" borderRadius={2} border="1px solid #ef5350">
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                      <Typography variant="subtitle2" color="error" fontWeight="bold">
                        Datos adicionales:
                      </Typography>
                      {reglasArticulo.sugiereLoteSerie && (
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          startIcon={<SearchIcon />}
                          onClick={() => {
                            if (reglasArticulo.lote) mutConsultarLotesSugeridos()
                            else if (reglasArticulo.serie) mutConsultarSeriesSugeridas()
                          }}
                        >
                          Consultar Sugerencias
                        </Button>
                      )}
                    </Box>

                    {/* BLOQUE LOTES */}
                    {reglasArticulo.lote && (
                      <>
                        <TextField
                          inputRef={loteInputRef}
                          fullWidth
                          label="Lote"
                          size="small"
                          value={captura.lote}
                          onFocus={() => setTargetField("lote")}
                          onChange={(e) => handleInputChange("lote", e.target.value)}
                          inputProps={{ enterKeyHint: "next" }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault()
                              if (caducidadInputRef.current) caducidadInputRef.current.focus()
                            }
                          }}
                          sx={{ mb: 2, bgcolor: "white" }}
                        />
                        <TextField
                          fullWidth
                          type="date"
                          label="Fecha de Fabricación"
                          size="small"
                          InputLabelProps={{ shrink: true }}
                          value={captura.fabricacion}
                          onChange={(e) => handleInputChange("fabricacion", e.target.value)}
                          sx={{ mb: 2, bgcolor: "white" }}
                        />
                        <TextField
                          inputRef={caducidadInputRef}
                          fullWidth
                          type="date"
                          label="Fecha de Caducidad"
                          size="small"
                          InputLabelProps={{ shrink: true }}
                          value={captura.caducidad}
                          onChange={(e) => handleInputChange("caducidad", e.target.value)}
                          inputProps={{ enterKeyHint: "go" }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") handleSubmit()
                          }}
                          sx={{ mb: 2, bgcolor: "white" }}
                        />
                      </>
                    )}

                    {/* BLOQUE SERIES */}
                    {reglasArticulo.serie && (
                      <>
                        <Divider sx={{ my: 2 }} />
                        <Typography variant="caption" display="block" color="error" mb={1}>
                          * La cantidad está forzada a 1 unidad.
                        </Typography>
                        <TextField
                          inputRef={serieInputRef}
                          fullWidth
                          label="Número de Serie *"
                          size="small"
                          value={captura.serie}
                          onFocus={() => setTargetField("serie")}
                          onChange={(e) => handleInputChange("serie", e.target.value)}
                          sx={{ mb: 2, bgcolor: "white" }}
                        />

                        {/* CAMPOS VEHÍCULO */}
                        {reglasArticulo.esVehiculo && (
                          <>
                            <TextField
                              fullWidth
                              label="Motor *"
                              size="small"
                              value={captura.sermotor}
                              onChange={(e) => handleInputChange("sermotor", e.target.value)}
                              sx={{ mb: 2, bgcolor: "white" }}
                            />
                            <TextField
                              fullWidth
                              label="RAM (Chasis)"
                              size="small"
                              value={captura.serram}
                              onChange={(e) => handleInputChange("serram", e.target.value)}
                              sx={{ mb: 2, bgcolor: "white" }}
                            />
                            <Grid container spacing={2} mb={2}>
                              <Grid item xs={4}>
                                <TextField
                                  fullWidth
                                  label="Año Mod."
                                  type="number"
                                  size="small"
                                  value={captura.serModAnio}
                                  onChange={(e) => handleInputChange("serModAnio", e.target.value)}
                                  sx={{ bgcolor: "white" }}
                                />
                              </Grid>
                              <Grid item xs={4}>
                                <TextField
                                  fullWidth
                                  label="Año Fab."
                                  type="number"
                                  size="small"
                                  value={captura.serProAnio}
                                  onChange={(e) => handleInputChange("serProAnio", e.target.value)}
                                  sx={{ bgcolor: "white" }}
                                />
                              </Grid>
                              <Grid item xs={4}>
                                <TextField
                                  fullWidth
                                  label="Mes Fab."
                                  type="number"
                                  size="small"
                                  value={captura.serProMes}
                                  onChange={(e) => handleInputChange("serProMes", e.target.value)}
                                  sx={{ bgcolor: "white" }}
                                />
                              </Grid>
                            </Grid>
                            <TextField
                              select
                              fullWidth
                              label="Color *"
                              size="small"
                              value={captura.colcodigo}
                              onChange={(e) => handleInputChange("colcodigo", e.target.value)}
                              sx={{ mb: 2, bgcolor: "white" }}
                            >
                              {colores.map((c) => (
                                <MenuItem key={c.colcodigo} value={c.colcodigo}>
                                  {c.coldescri}
                                </MenuItem>
                              ))}
                            </TextField>
                            <TextField
                              select
                              fullWidth
                              label="País Origen"
                              size="small"
                              value={captura.paiscodigo}
                              onChange={(e) => handleInputChange("paiscodigo", e.target.value)}
                              sx={{ mb: 2, bgcolor: "white" }}
                            >
                              {paises.map((p) => (
                                <MenuItem key={p.paiscodigo} value={p.paiscodigo}>
                                  {p.paisdescri}
                                </MenuItem>
                              ))}
                            </TextField>
                          </>
                        )}
                      </>
                    )}
                  </Box>
                </Collapse>
              </Grid>

              <Grid item xs={12} mt={1}>
                <Button
                  variant="contained"
                  color={isOffline ? "secondary" : "primary"}
                  fullWidth
                  size="large"
                  startIcon={<SaveIcon />}
                  onClick={handleSubmit}
                  disabled={isSaving || isValidating}
                  sx={{ py: 1.5, fontSize: "1.1rem", borderRadius: 2, fontWeight: "bold" }}
                >
                  {isOffline ? "GUARDAR EN CELULAR" : "GUARDAR LECTURA"}
                </Button>
              </Grid>
            </Grid>
          </Paper>
        </Box>

        {/* ================= MODAL LOTES SUGERIDOS ================= */}
        <Dialog open={modalLotesOpen} onClose={() => setModalLotesOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ bgcolor: "#f5f5f5", color: "primary.main", fontWeight: "bold" }}>
            Lotes Sugeridos
          </DialogTitle>
          <DialogContent dividers sx={{ p: 0 }}>
            <List>
              {lotesSugeridos.map((lote, index) => (
                <ListItem
                  key={index}
                  button
                  divider
                  onDoubleClick={() => {
                    handleInputChange("lote", lote.artlote)

                    // Limpiar fechas antes de setear el nuevo lote
                    // para que no queden datos del lote anterior seleccionado.
                    handleInputChange("fabricacion", "")
                    handleInputChange("caducidad", "")

                    if (lote.artfecfab) handleInputChange("fabricacion", dayjs(lote.artfecfab).format("YYYY-MM-DD"))
                    if (lote.artfecven) handleInputChange("caducidad", dayjs(lote.artfecven).format("YYYY-MM-DD"))

                    setModalLotesOpen(false)
                  }}
                >
                  <ListItemText
                    primary={
                      <Typography fontWeight="bold" color="primary">
                        Lote: {lote.artlote}
                      </Typography>
                    }
                    secondary={`Fab: ${lote.artfecfab ? dayjs(lote.artfecfab).format("DD/MM/YYYY") : "N/A"} | Ven: ${lote.artfecven ? dayjs(lote.artfecven).format("DD/MM/YYYY") : "N/A"}`}
                  />
                </ListItem>
              ))}
            </List>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setModalLotesOpen(false)}>Cerrar</Button>
          </DialogActions>
        </Dialog>

        {/* ================= MODAL SERIES SUGERIDAS ================= */}
        <Dialog open={modalSeriesOpen} onClose={() => setModalSeriesOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ bgcolor: "#f5f5f5", color: "primary.main", fontWeight: "bold" }}>
            Series Sugeridas
          </DialogTitle>
          <DialogContent dividers sx={{ p: 0 }}>
            <List>
              {seriesSugeridas.map((serie, index) => (
                <ListItem
                  key={index}
                  button
                  divider
                  onDoubleClick={() => {
                    handleInputChange("serie", serie.sernumero)

                    // Limpiar siempre los campos de vehículo antes de setear la nueva serie,
                    // para que no queden datos de la serie anterior seleccionada.
                    handleInputChange("sermotor", "")
                    handleInputChange("serram", "")
                    handleInputChange("serModAnio", "")
                    handleInputChange("serProAnio", "")
                    handleInputChange("serProMes", "")
                    handleInputChange("colcodigo", "")
                    handleInputChange("paiscodigo", "")

                    // Luego, si es vehículo, llenar con los datos de la serie
                    if (serie.TIPSERIE === "V") {
                      handleInputChange("sermotor", serie.sermotor || "")
                      handleInputChange("serram", serie.serram || "")
                      handleInputChange("serModAnio", serie.serModAnio || "")
                      handleInputChange("serProAnio", serie.serProAnio || "")
                      handleInputChange("serProMes", serie.serProMes || "")
                      handleInputChange("colcodigo", serie.colcodigo || "")
                      handleInputChange("paiscodigo", serie.paiscodigo || "")
                    }
                    setModalSeriesOpen(false)
                  }}
                >
                  <ListItemText
                    primary={
                      <Typography fontWeight="bold" color="primary">
                        Serie: {serie.sernumero}
                      </Typography>
                    }
                    secondary={
                      serie.TIPSERIE === "V"
                        ? `Motor: ${serie.sermotor || "N/A"} | Color: ${serie.colcodigo || "N/A"}`
                        : "Serie Estándar"
                    }
                  />
                </ListItem>
              ))}
            </List>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setModalSeriesOpen(false)}>Cerrar</Button>
          </DialogActions>
        </Dialog>

        {/* ================= MODAL GESTOR DE COLA OFFLINE ================= */}
        <Dialog open={modalColaOpen} onClose={() => setModalColaOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ bgcolor: "#f5f5f5", color: "primary.main", fontWeight: "bold" }}>
            Lecturas Pendientes ({listaCola.length})
          </DialogTitle>
          <DialogContent dividers sx={{ p: 0 }}>
            {listaCola.length === 0 ? (
              <Box p={3} textAlign="center">
                <Typography color="textSecondary">No hay registros pendientes.</Typography>
              </Box>
            ) : (
              <List>
                {listaCola.map((item, index) => (
                  <React.Fragment key={index}>
                    <ListItem
                      secondaryAction={
                        <Box>
                          <Tooltip title="Corregir Registro">
                            <IconButton edge="end" color="primary" onClick={() => handleEditarDeCola(index)}>
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Descartar Registro">
                            <IconButton edge="end" color="error" onClick={() => handleEliminarDeCola(index)}>
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      }
                    >
                      <ListItemText
                        primary={
                          <Typography fontWeight="bold" color="primary">
                            {item.artcodigo}{" "}
                            <span style={{ color: "black", fontWeight: "normal" }}>x {item.cantidad} und</span>
                          </Typography>
                        }
                        secondary={
                          <>
                            {item.serie && (
                              <Typography variant="caption" display="block">
                                Serie: {item.serie} {item.sermotor ? `| Motor: ${item.sermotor}` : ""}
                              </Typography>
                            )}
                            {item.lote && (
                              <Typography variant="caption" display="block">
                                Lote: {item.lote} (Vence: {item.caducidad})
                              </Typography>
                            )}
                          </>
                        }
                      />
                    </ListItem>
                    <Divider />
                  </React.Fragment>
                ))}
              </List>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2, justifyContent: "space-between" }}>
            <Button onClick={() => setModalColaOpen(false)} color="inherit">
              Cerrar
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={<Sync />}
              onClick={sincronizarCola}
              disabled={listaCola.length === 0 || isOffline}
            >
              Sincronizar Todas
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </ThemeProvider>
  )
}

export default ScannerMobileIndex
